import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ComplianceFooter } from "@/components/ComplianceBanner";
import { LandingHero } from "@/components/LandingHero";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Check, Crown, Layers, Flame, Zap, Grid3x3, Radar, Send, ListChecks, MessageCircle } from "lucide-react";

export const WA = "919953258056";
export const waLink = (msg) => `https://wa.me/${WA}?text=${encodeURIComponent(msg)}`;
export const PLAN_MSG = "Hi TFR, I'd like to claim a Founding Member seat on the TFR Institutional plan (₹7,999/month, locked for life).";
export const PRICE = { founding: "₹7,999", regular: "₹12,999", seats: 20 };

export const ENGINES = ["TFR Range Matrix", "TFR Node-H", "TFR Node-L", "TFR Flow Zone", "TFR Band", "Blue Elephant Volume", "Opening Thrust", "TFR Prime", "Big Money Volume", "TFR Breakout"];

export const FEATURES = [
  { img: "/landing/gold-art.webp", Icon: Crown, tag: "SWING · INTRADAY", t: "TFR Gold", d: "Our highest-conviction setups — when TFR's proprietary levels line up across timeframes and price commits. Every setup is logged, so the Gold Scorecard shows the real hit rate." },
  { img: "/landing/nodes-art.webp", Icon: Layers, tag: "TFR NODE-H · NODE-L", t: "TFR Value Nodes", d: "TFR's proprietary engine marks where desks accepted price — the Node-H ceiling and Node-L floor. The scanner surfaces the moment a stock leaves that zone, across F&O stocks and the major indices." },
  { img: "/landing/power-art.webp", Icon: Flame, tag: "TFR RANGE MATRIX", t: "Power Scan", d: "Stocks breaking out of their TFR Range Matrix with every other TFR engine agreeing — the shortlist of names where institutional flow is pushing hardest." },
  { img: "/landing/elephant-art.webp", Icon: Radar, tag: "NIFTY 500", t: "Blue Elephant Volume", d: "TFR's footprint detector for institutional-size participation across the Nifty 500 — separating genuine accumulation from noise." },
  { img: "/landing/heatmap-art.webp", Icon: Grid3x3, tag: "SECTOR FLOW", t: "Heatmap & Sector Pulse", d: "See which sectors money is rotating into and out of, with the leading and lagging sectors tagged on every scanner row." },
];

export const TOOLS = [
  [Radar, "Institutional Scanner", "TFR Flow Zone setups before expansion"],
  [Zap, "Opening Thrust", "Conviction at the opening bell"],
  [Layers, "TFR Value Nodes", "Node-H / Node-L breaks as they happen"],
  [Crown, "TFR Gold", "Multi-timeframe TFR alignment"],
  [Flame, "Power Scan", "Every TFR engine in agreement"],
  [Grid3x3, "Heatmap", "Sector-wise F&O money flow"],
  [Send, "Telegram alerts", "TFR signals pushed to your phone"],
  [ListChecks, "Watchlists", "Dated lists with % since added"],
];

export const INCLUDED = ["Every TFR scanner — Gold, Value Nodes, Power Scan, Opening Thrust, Institutional", "Live F&O market data through the session", "TFR levels plotted on every chart", "Nifty 500 Blue Elephant scan", "Telegram alerts and day-end summary", "Watchlists and one-click TradingView export"];

const G = "#C9A24A";
const serif = "font-['Instrument_Serif',serif] font-normal";
const up = { hidden: { opacity: 0, y: 30 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }) };
const SPANS = ["md:col-span-4 md:row-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-3", "md:col-span-3"];
const Eyebrow = ({ children }) => <div className="text-[11px] tracking-[0.4em]" style={{ color: G }}>{children}</div>;
const Rule = () => <div className="mx-auto flex max-w-xs items-center gap-4"><span className="h-px flex-1 bg-[#C9A24A]/40" /><span className="text-xs" style={{ color: G }}>◆</span><span className="h-px flex-1 bg-[#C9A24A]/40" /></div>;

const Bento = ({ f, i }) => (
  <motion.div variants={up} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} custom={i % 3}
    className={`group relative min-h-[320px] overflow-hidden border border-[#C9A24A]/25 ${i === 0 ? "rounded-t-[200px] md:rounded-t-[320px]" : ""} ${SPANS[i]}`} data-testid={`feature-${i}`}>
    <img src={f.img} alt={f.t} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
    <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-[#0A0907] via-[#0A0907]/85 to-transparent" />
    <div className="absolute bottom-0 p-7">
      <div className="text-[10px] tracking-[0.35em]" style={{ color: G }}>{f.tag}</div>
      <h3 className={`${serif} mt-2 text-4xl text-[#F5EEDC]`}>{f.t}</h3>
      <p className={`mt-2 text-sm leading-relaxed text-[#F5EEDC]/65 ${i === 0 ? "max-w-md" : "max-w-sm line-clamp-3"}`}>{f.d}</p>
    </div>
  </motion.div>
);

const Engines = () => (
  <section id="features" className="max-w-7xl mx-auto px-6 py-28">
    <div className="text-center"><Eyebrow>PROPRIETARY ENGINES</Eyebrow><h2 className={`${serif} mt-4 text-5xl sm:text-6xl text-[#F5EEDC]`}>Five engines. One desk view.</h2></div>
    <div className="mt-16 grid md:grid-cols-6 md:auto-rows-[320px] gap-4">{FEATURES.map((f, i) => <Bento key={f.t} f={f} i={i} />)}</div>
  </section>
);

const EngineLine = () => (
  <section className="border-y border-[#C9A24A]/20 py-14 px-6 text-center" data-testid="engine-strip">
    <p className={`${serif} mx-auto max-w-5xl text-3xl sm:text-4xl leading-snug text-[#F5EEDC]/80`}>
      {ENGINES.map((e, i) => <span key={e} className="whitespace-nowrap">{e}{i < ENGINES.length - 1 && <span className="mx-3" style={{ color: G }}>·</span>}</span>)}
    </p>
  </section>
);

const Desk = () => (
  <section id="tools" className="max-w-7xl mx-auto px-6 py-28">
    <Eyebrow>THE FULL DESK</Eyebrow>
    <div className="mt-8 divide-y divide-[#C9A24A]/15 border-y border-[#C9A24A]/15">
      {TOOLS.map(([Icon, t, d], i) => (
        <div key={t} className="group grid grid-cols-12 items-center py-6 transition-colors hover:bg-[#C9A24A]/[0.04]" data-testid={`tool-${i}`}>
          <span className="col-span-2 sm:col-span-1 font-mono text-sm text-[#F5EEDC]/30">{String(i + 1).padStart(2, "0")}</span>
          <span className={`${serif} col-span-10 sm:col-span-5 text-3xl text-[#F5EEDC] transition-colors group-hover:text-[#C9A24A]`}>{t}</span>
          <span className="col-span-12 sm:col-span-6 mt-1 sm:mt-0 text-[#F5EEDC]/55">{d}</span>
        </div>
      ))}
    </div>
  </section>
);

const Pricing = () => (
  <section id="pricing" className="relative overflow-hidden border-y border-[#C9A24A]/20 py-28">
    <img src="/landing/gold-art.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-[#0A0907]/40" />
    <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <div className="inline-flex items-center gap-2 border border-[#C9A24A]/50 bg-[#C9A24A]/10 px-3 py-1.5 text-[10px] tracking-[0.3em]" style={{ color: G }} data-testid="founding-badge">
          <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: G }} />FOUNDING MEMBER · ONLY {PRICE.seats} SEATS
        </div>
        <div className="mt-6 flex items-baseline gap-5">
          <div className={`${serif} text-8xl sm:text-9xl leading-none text-[#F5EEDC]`} data-testid="plan-price">{PRICE.founding}</div>
          <div className={`${serif} text-3xl text-[#F5EEDC]/35 line-through decoration-[#C9A24A]/70`} data-testid="plan-regular-price">{PRICE.regular}</div>
        </div>
        <div className="mt-3 text-sm tracking-[0.3em] text-[#F5EEDC]/50">PER MONTH · LOCKED FOR LIFE</div>
        <p className="mt-5 max-w-md text-[#F5EEDC]/60">The first {PRICE.seats} members keep {PRICE.founding}/month for as long as they stay subscribed. After that, the plan is {PRICE.regular}/month.</p>
      </div>
      <div className="border border-[#C9A24A]/40 bg-[#0A0907]/70 backdrop-blur-xl p-8 sm:p-10" data-testid="plan-card">
        <div className="text-[11px] tracking-[0.35em] text-[#F5EEDC]/50">TFR INSTITUTIONAL · FOUNDING MEMBER</div>
        <ul className="mt-6 space-y-3.5">{INCLUDED.map((x) => <li key={x} className="flex gap-3 text-[#F5EEDC]/80"><Check className="h-5 w-5 shrink-0" style={{ color: G }} />{x}</li>)}</ul>
        <a href={waLink(PLAN_MSG)} target="_blank" rel="noopener noreferrer" className="mt-9 flex items-center justify-center py-4 text-xs tracking-[0.35em] text-black transition-[filter,box-shadow] hover:brightness-110 hover:shadow-[0_0_60px_-12px_#C9A24A]" style={{ background: G }} data-testid="plan-whatsapp">
          <MessageCircle className="mr-3 h-4 w-4" />CLAIM A FOUNDING SEAT ON WHATSAPP
        </a>
        <p className="mt-4 text-center text-xs text-[#F5EEDC]/40">Analytics & research tool only — not SEBI-registered advice.</p>
      </div>
    </div>
  </section>
);


export const FAQS = [
  ["ACCESS", "How do I get access?", "Message the desk on WhatsApp from the button above. Once your ₹7,999 founding-member payment is confirmed, we activate your account — usually the same day — and you sign in with your email."],
  ["ACCESS", "Can I cancel?", "Yes, cancel anytime. Your access continues until the end of the month you have paid for. Payments already made are not refunded."],
  ["ACCESS", "Is this investment advice?", "No. TFR is an analytics and research tool. Every mark is a quantitative observation, not a buy or sell tip. We are not SEBI-registered investment advisers."],
  ["DATA", "Where does the data come from?", "Live market data from a SEBI-registered broker feed and NSE's official end-of-day files. No simulated or sample data is ever shown."],
  ["DATA", "How often do scanners update?", "Continuously through market hours. TFR levels are rebuilt after every close."],
];

const Faq = () => (
  <section id="faq" className="max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-12 gap-12">
    <div className="lg:col-span-4">
      <Eyebrow>QUESTIONS</Eyebrow>
      <h2 className={`${serif} mt-4 text-5xl sm:text-6xl text-[#F5EEDC]`}>Asked at <em style={{ color: G }}>the desk.</em></h2>
      <p className="mt-5 text-[#F5EEDC]/55">Anything else? Message us on WhatsApp.</p>
    </div>
    <Accordion type="single" collapsible className="lg:col-span-8 border-t border-[#C9A24A]/20" data-testid="faq-list">
      {FAQS.map(([tag, q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-b border-[#C9A24A]/20">
          <AccordionTrigger className="py-6 text-left hover:no-underline [&>svg]:text-[#C9A24A]" data-testid={`faq-q-${i}`}>
            <span className="flex items-baseline gap-5">
              <span className="hidden sm:inline w-16 shrink-0 text-[10px] tracking-[0.3em]" style={{ color: G }}>{tag}</span>
              <span className={`${serif} text-2xl text-[#F5EEDC]`}>{q}</span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-6 sm:pl-[6.25rem] text-base leading-relaxed text-[#F5EEDC]/65" data-testid={`faq-a-${i}`}>{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const FinalCta = () => (
  <section className="py-28 px-6 text-center">
    <Rule />
    <h2 className={`${serif} mt-12 text-5xl sm:text-7xl text-[#F5EEDC]`}>The private desk, <em style={{ color: G }}>now yours.</em></h2>
    <p className="mt-5 text-[#F5EEDC]/60">Made by desk institutional flow traders, for traders who want the same view.</p>
    <a href={waLink("Hi TFR, I'd like access to the TFR Institutional Scanner.")} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
      className="mt-10 inline-flex items-center border px-9 py-4 text-xs tracking-[0.35em] transition-colors hover:bg-[#C9A24A] hover:text-black" style={{ borderColor: G, color: G }}>
      TALK TO THE DESK ON WHATSAPP
    </a>
  </section>
);

export default function Landing() {
  const navigate = useNavigate();
  const signIn = () => navigate("/login");
  return (
    <div className="tfr-landing min-h-screen bg-[#0A0907] text-[#F5EEDC] overflow-x-hidden" data-testid="landing-page">
      <button onClick={() => { document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }); setTimeout(() => document.querySelector("[data-testid=plan-whatsapp]")?.animate([{ transform: "scale(1)" }, { transform: "scale(1.04)" }, { transform: "scale(1)" }], { duration: 700, iterations: 2 }), 900); }}
        className="group relative z-20 block w-full bg-[#C9A24A] px-4 py-2 text-center text-[11px] sm:text-xs font-semibold tracking-[0.15em] text-[#0A0907] transition-[filter] hover:brightness-110" data-testid="founding-banner">
        FOUNDING MEMBER OFFER · ONLY {PRICE.seats} SEATS · {PRICE.founding}/MONTH LOCKED FOR LIFE <span className="hidden sm:inline opacity-60 line-through">{PRICE.regular}</span>
        <span className="ml-2 underline underline-offset-4 group-hover:no-underline">CLAIM YOUR SEAT →</span>
      </button>
      <div className="relative z-20 border-b border-[#C9A24A]/15 bg-[#0A0907] py-1.5 text-center font-mono text-[10.5px] tracking-wide text-[#F5EEDC]/45">
        ANALYTICS &amp; RESEARCH TOOL ONLY · NOT SEBI-REGISTERED INVESTMENT ADVICE OR TIPS
      </div>
      <LandingHero onSignIn={signIn} />
      <Engines />
      <EngineLine />
      <Desk />
      <Pricing />
      <Faq />
      <FinalCta />
      <div className="tfr-dark-footer"><ComplianceFooter /></div>
    </div>
  );
}
